import type { ReactNode } from 'react'
import { cn } from '@/lib/utils'
import googleAdsIcon from '@/assets/hero/google-ads-icon.svg'

/**
 * Small pieces shared by the Attribution states (Paper "03 - App ·
 * Attribution"): the callout banners, brand marks, the funnel arrow and the
 * "Needs Stripe" placeholders.
 */

/** Stripe's mark as Paper draws it: a rounded square with the white S. */
export function StripeMark({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 16 16" className={cn('size-4 shrink-0', className)} aria-hidden>
      <rect width="16" height="16" rx="4" fill="#635BFF" />
      <path
        d="M7.4 6.3c0-.4.3-.6.9-.6.8 0 1.7.2 2.5.7V4.2a6.6 6.6 0 0 0-2.5-.5C6.3 3.7 5 4.7 5 6.4c0 2.6 3.6 2.2 3.6 3.3 0 .5-.4.6-1 .6-.9 0-1.9-.4-2.7-.8v2.2c.9.4 1.8.6 2.7.6 2.1 0 3.5-1 3.5-2.7C11.1 6.8 7.4 7.3 7.4 6.3Z"
        fill="#FFFFFF"
      />
    </svg>
  )
}

export function MetaMark({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={cn('size-4 shrink-0', className)} aria-hidden>
      <path
        d="M6.2 7C3.6 7 2 10 2 12.6 2 15 3.3 16.6 5.2 16.6c1.7 0 2.9-1.4 4.6-4.3l1.3-2.2C12.8 7.3 14.3 7 15.7 7 18.6 7 22 9.8 22 13.3c0 2-1 3.3-2.7 3.3-1.6 0-2.7-1.2-4.6-4.3l-1.4-2.3C11.6 7.6 10.1 7 8.4 7z"
        fill="none"
        stroke="#0081FB"
        strokeWidth="2.1"
        strokeLinejoin="round"
      />
    </svg>
  )
}

/** Google Ads' official mark (the same file the marketing hero uses), not a redraw. */
export function GoogleAdsMark({ className }: { className?: string }) {
  return <img src={googleAdsIcon} alt="" aria-hidden className={cn('size-4 shrink-0', className)} />
}

/** The thin arrow between funnel steps. */
export function StepArrow({ faint }: { faint?: boolean }) {
  return (
    <svg width="40" height="8" viewBox="0 0 40 8" aria-hidden className={faint ? 'text-foreground/15' : 'text-foreground/25'}>
      <path d="M0 4h36m-3-3 3 3-3 3" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

/**
 * Amber needs-attention banner ("Google Ads needs reconnecting", "Stripe
 * stopped sharing payments"). `inline` puts title and body on one baseline.
 */
export function WarningBanner({
  icon,
  title,
  body,
  aside,
  action,
  inline,
}: {
  icon?: ReactNode
  title: ReactNode
  body?: ReactNode
  aside?: ReactNode
  action?: ReactNode
  inline?: boolean
}) {
  return (
    <div
      role="status"
      className={cn(
        'flex flex-wrap items-center gap-3 rounded-[10px] bg-warning-tint pl-3.5 ring-1 ring-warning/20 sm:flex-nowrap',
        inline ? 'py-2 pr-3.5' : 'py-2.5 pr-2.5',
      )}
    >
      {icon}
      <div
        className={cn(
          'flex min-w-0 flex-1 basis-0',
          inline ? 'flex-wrap items-baseline gap-x-2 gap-y-0.5' : 'flex-col gap-0.5',
        )}
      >
        <span className="text-sm font-semibold leading-[18px] text-warning">{title}</span>
        {body && <span className="text-[13px] leading-4 text-warning">{body}</span>}
      </div>
      {aside && <span className="text-xs leading-4 text-warning">{aside}</span>}
      {action}
    </div>
  )
}

/** White callout with a hairline ring: "Connect Stripe to see what ads earned". */
export function CalloutCard({
  icon,
  title,
  body,
  aside,
  action,
}: {
  icon?: ReactNode
  title: ReactNode
  body?: ReactNode
  aside?: ReactNode
  action?: ReactNode
}) {
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-[10px] bg-card py-2.5 pl-3.5 pr-2.5 shadow-[0_1px_2px_rgb(0_0_0/0.03)] ring-1 ring-border sm:flex-nowrap">
      {icon}
      <div className="flex min-w-0 flex-1 basis-0 flex-col gap-0.5">
        <span className="text-sm font-semibold leading-[18px] text-foreground">{title}</span>
        {body && <span className="text-[13px] leading-4 text-muted-foreground">{body}</span>}
      </div>
      {aside && <span className="text-xs leading-4 text-ui-meta">{aside}</span>}
      {action}
    </div>
  )
}

/** Muted tip row on the surface grey: "Running ads? Connect Meta or Google Ads…". */
export function TipRow({ lead, body, action }: { lead: ReactNode; body: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-xl bg-muted/60 px-4 py-3 ring-1 ring-border sm:flex-nowrap">
      <p className="min-w-0 flex-1 basis-0 text-[13px] leading-4">
        <span className="font-medium text-foreground">{lead}</span>{' '}
        <span className="text-muted-foreground">{body}</span>
      </p>
      {action}
    </div>
  )
}

/** "Needs Stripe" in a KPI tile's value slot. */
export function NeedsRevenueValue({ provider = 'Stripe' }: { provider?: string }) {
  return <span className="text-sm font-medium tracking-normal text-ui-meta">Needs {provider}</span>
}

/** A KPI value with nothing to show yet ("None yet", "—"). */
export function EmptyValue({ children }: { children: ReactNode }) {
  return <span className="text-ui-meta">{children}</span>
}

/** The money tiles across the top of the report card (Paper: hairline rules, ink underline on the focus tile). */
export function MoneyTiles({ children }: { children: ReactNode }) {
  return <div className="flex overflow-x-auto scrollbar-hide border-b border-ui-hairline">{children}</div>
}

/**
 * One money tile. `selected` marks the series the chart below plots, in ink:
 * orange stays free for the view's one primary action.
 */
export function MoneyTile({
  label,
  value,
  suffix,
  first,
  selected,
}: {
  label: string
  value: ReactNode
  /** Small note after the value: "+18%", "$446 a day" */
  suffix?: ReactNode
  first?: boolean
  selected?: boolean
}) {
  return (
    <div
      className={cn(
        'flex min-w-[140px] flex-1 basis-0 flex-col gap-1.5 px-[18px] py-4',
        !first && 'border-l border-ui-hairline',
        selected && 'shadow-[inset_0_-1.5px_0_color-mix(in_oklab,var(--ui-title)_80%,transparent)]',
      )}
    >
      <span className="whitespace-nowrap text-xs leading-4 text-muted-foreground">{label}</span>
      <span className="flex min-w-0 items-baseline gap-1.5">
        <span className="truncate text-[22px] font-semibold leading-7 tracking-[-0.02em] text-foreground tabular-nums">
          {value}
        </span>
        {suffix && <span className="shrink-0 text-xs leading-4 text-ui-meta tabular-nums">{suffix}</span>}
      </span>
    </div>
  )
}
